// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Vault-key gate — biometric / master-password guard around the derived
 * AES vault key.
 *
 * The derived key (from `crypto/key-derivation.ts`, in-memory only via
 * `client/src/lib/vault-keys.ts`) decrypts every piece of vault ciphertext.
 * On native (iOS / Android via Capacitor) we additionally gate access
 * behind:
 *
 *   1. A biometric prompt (Face ID / Touch ID / fingerprint), when an
 *      installed plugin advertises the capability via the
 *      `pssmngr:biometric` runtime hook (see
 *      `client/src/native/biometric.ts`). If biometrics are not available
 *      we fall back to the existing master-password modal which re-derives
 *      the key client-side per `crypto/key-derivation.ts`.
 *
 *   2. A "locked" state that is re-entered on:
 *        - cold start (native only),
 *        - the Capacitor app moving to background (`appStateChange`,
 *          wired by `client/src/native/lifecycle.ts`),
 *        - an idle timeout (delegated to the existing vault-crypto
 *          service which already manages auto-lock).
 *
 * Threat-model constraints (zero-knowledge):
 *   - The derived key is NEVER persisted in `Preferences`, `localStorage`,
 *     IndexedDB, the service-worker cache, or any other storage. Re-running
 *     `unlock()` re-derives it.
 *   - The key is NEVER attached to `window` / `globalThis`. Holding it is
 *     delegated to the existing module-private `vault-keys` store.
 *   - The key is NEVER logged or sent to Sentry.
 */

import { vaultCrypto } from "@/services/vault-crypto";
import { hasVaultKey, clearVaultKey } from "@/lib/vault-keys";
import { isEnabled as isPersistentUnlockEnabled } from "@/lib/persistent-unlock";
import { native } from "@/native";

/* ─────────────────────────────────────────────────────────────────── */
/*  Biometric runtime hook                                             */
/* ─────────────────────────────────────────────────────────────────── */

/**
 * Optional biometric adapter. Plugins (e.g. capacitor-native-biometric)
 * are wired via this hook rather than imported directly so this module
 * stays installable even when no biometric plugin is present.
 *
 * The hook is intentionally *very* small — implementations should NOT
 * touch the vault key themselves; they only signal user presence.
 */
export interface BiometricAdapter {
  isAvailable(): Promise<boolean>;
  /** Resolves true on success, false on user-cancel; throws on hard error. */
  verify(reason: string): Promise<boolean>;
}

let _biometricAdapter: BiometricAdapter | null = null;

export function registerBiometricAdapter(adapter: BiometricAdapter | null): void {
  _biometricAdapter = adapter;
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Master-password fallback hook                                      */
/* ─────────────────────────────────────────────────────────────────── */

/**
 * Bridge to the existing master-password re-entry modal. The UI layer
 * installs the prompt; this gate calls into it whenever biometrics are
 * unavailable or the user cancels.
 *
 * The promise must resolve with the master password (string) or null on
 * cancel. The password is then passed straight to vault-crypto's existing
 * `unlock()` flow which re-derives the key client-side and never sends it
 * to the server.
 */
export type MasterPasswordPrompt = () => Promise<string | null>;

let _masterPasswordPrompt: MasterPasswordPrompt | null = null;

export function registerMasterPasswordPrompt(prompt: MasterPasswordPrompt | null): void {
  _masterPasswordPrompt = prompt;
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Public API                                                         */
/* ─────────────────────────────────────────────────────────────────── */

export interface UnlockOptions {
  /** Human-readable reason shown by the biometric prompt. */
  reason?: string;
  /**
   * Force the master-password fallback even when biometrics are
   * available. Used by the settings UI for explicit re-derivation.
   */
  forcePassword?: boolean;
}

export type UnlockOutcome =
  | { ok: true; via: "already-unlocked" | "biometric" | "password" }
  | { ok: false; reason: "cancelled" | "no-prompt-installed" | "biometric-failed" };

/**
 * Decrypts using the in-memory key only if currently unlocked. The
 * `vaultCrypto` service is the single owner of the actual key material;
 * we just gate access to it.
 */
export function isUnlocked(): boolean {
  return vaultCrypto.isUnlocked();
}

/**
 * Drop the in-memory key. Called on app background, idle timeout, logout,
 * and as a defensive measure on cold start.
 */
export function lock(): void {
  // Delegate to vault-crypto so its listeners (UI state) fire too.
  vaultCrypto.lock();
  // Defensive: also wipe the raw key store in case anyone bypassed
  // vault-crypto (should never happen, but cheap insurance).
  clearVaultKey();
}

/**
 * Open the vault. On native this is gated by biometrics or the
 * master-password fallback; on web it is purely a master-password flow
 * (the existing modal). Returns a structured outcome rather than
 * throwing, so the caller can distinguish "user cancelled" from
 * "biometric hardware error".
 */
export async function unlock(options: UnlockOptions = {}): Promise<UnlockOutcome> {
  if (isUnlocked()) {
    return { ok: true, via: "already-unlocked" };
  }

  const useBiometric = native.isNative && !options.forcePassword;

  if (useBiometric && _biometricAdapter) {
    try {
      const available = await _biometricAdapter.isAvailable();
      if (available) {
        const verified = await _biometricAdapter.verify(
          options.reason ?? "Unlock your password vault",
        );
        if (verified && isUnlocked()) {
          // Vault-crypto cached the previously-derived key under a
          // platform-secured envelope (e.g. via the biometric adapter
          // implementation). The gate is satisfied; do not return the key.
          return { ok: true, via: "biometric" };
        }
        if (!verified) {
          // User cancelled biometric — fall through to master-password.
        }
      }
    } catch {
      // Hard biometric error — fall through to master-password fallback.
    }
  }

  if (!_masterPasswordPrompt) {
    return { ok: false, reason: "no-prompt-installed" };
  }

  const password = await _masterPasswordPrompt();
  if (!password) {
    return { ok: false, reason: "cancelled" };
  }

  // The existing vault-crypto.unlock() expects (password, salt, expectedKeyHash?).
  // We do not own those parameters here — the calling UI does. The
  // master-password prompt implementation is responsible for invoking
  // vault-crypto.unlock(...) before returning the password to us, which
  // mirrors the existing web flow. We only verify the result.
  if (isUnlocked()) {
    return { ok: true, via: "password" };
  }

  return { ok: false, reason: "biometric-failed" };
}

/**
 * Convenience: ensure the vault is unlocked before performing a
 * vault-touching operation. Throws a tagged error rather than silently
 * decrypting with a stale key.
 */
export async function requireUnlocked(reason?: string): Promise<void> {
  if (isUnlocked()) return;
  const outcome = await unlock({ reason });
  if (!outcome.ok) {
    const err = new Error("Vault is locked");
    (err as Error & { code?: string }).code = "VAULT_LOCKED";
    throw err;
  }
}

/**
 * Reports whether a wrapped, at-rest vault key currently exists on this
 * device (see `lib/persistent-unlock.ts`). The key material itself is
 * non-extractable; only the boolean is exposed here.
 *
 * NOTE: on native, this is the seam a biometric adapter should gate before
 * calling `tryRestore()` — biometrics must pass FIRST or persistence is
 * effectively unauthenticated.
 */
export async function isKeyPersistedAnywhere(): Promise<boolean> {
  return isPersistentUnlockEnabled();
}

/** Verify the underlying key store is actually empty after a lock(). */
export function assertLocked(): void {
  if (hasVaultKey()) {
    throw new Error("VaultKeyGate invariant violated: key present after lock()");
  }
}

export const vaultKeyGate = {
  isUnlocked,
  lock,
  unlock,
  requireUnlocked,
  registerBiometricAdapter,
  registerMasterPasswordPrompt,
};
